<script setup lang="ts">
import { ref } from 'vue';

// Variable Declarations
const isDragOver = ref(false);
const selectedFile = ref([] as File[]);
const fileInput = ref<HTMLInputElement | null>(null);
const currentSectionsExpanded = ref([] as string[]);
const fileAdded = ref(false);
const emailSent = ref(false);
const isSubmitting = ref(false);
const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const form = ref({
    name: '',
    section: '',
    email: '',
    faculty: '',
    pdf: [] as File[],
});

// Function Declarations
const expandsection = (section: string) => {
    if (currentSectionsExpanded.value.includes(section)) {
        currentSectionsExpanded.value = currentSectionsExpanded.value.filter((value) => value !== section);
    } else {
        currentSectionsExpanded.value.push(section);
    }
};

async function emailForm(formValues: { name: string; email: string; faculty: string; section: string; pdf: File[] }) {
  if (isSubmitting.value) {
    return;
  }

  const { name, email, faculty, section, pdf } = formValues;
  fileAdded.value = false;
  emailSent.value = false;

  if (!pdf || pdf.length === 0) {
    alert('Please upload at least one PDF file.');
    return;
  }

  const payload = new FormData();
  payload.append('name', name);
  payload.append('email', email);
  payload.append('faculty', faculty);
  payload.append('section', section);
  pdf.forEach((file) => payload.append('pdf', file));

  try {
    isSubmitting.value = true;
    const response = await fetch(`${apiBaseUrl}/api/submissions`, {
      method: 'POST',
      body: payload,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Submission failed:', response.status, errorText);
      return;
    }

    const result = await response.json();
    fileAdded.value = Array.isArray(result.fileUrls) && result.fileUrls.length > 0;
    emailSent.value = result.emailSent === true;

    if (fileAdded.value && emailSent.value) {
      form.value.name = '';
      form.value.email = '';
      form.value.faculty = '';
      form.value.section = '';
      form.value.pdf = [];
      selectedFile.value = [];
    }
  } catch (error) {
    console.error('Error submitting form:', error);
  } finally {
    isSubmitting.value = false;
  }
}

function onDragOver(event: DragEvent) {
    event.preventDefault();
    isDragOver.value = true;
}

function dragEnter(event: DragEvent) {
    event.preventDefault();
    isDragOver.value = true;
}

function dragLeave(event: DragEvent) {
    event.preventDefault();
    isDragOver.value = false;
}

function onDrop(event: DragEvent) {
    event.preventDefault();
    isDragOver.value = false;
    const files = event.dataTransfer?.files;
    if (files) handleFiles(files);
}

function onFileChange(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (files) handleFiles(files);
}

function handleFiles(files: FileList) {
    if (!form.value.pdf) {
        form.value.pdf = [];
    }
    for (let i = 0; i < files.length; i++) {
        if (files[i].type === 'application/pdf') {
            if (selectedFile.value) {
                selectedFile.value.push(files[i]);
            }
            form.value.pdf.push(files[i]);
        } else {
            alert('Please upload a PDF file.');
        }
    }
}

function removeFile(index: number) {
    if (form.value.pdf) {
        selectedFile.value.splice(index, 1);
        form.value.pdf.splice(index, 1);
    }
}

function browseFiles() {
    fileInput.value?.click();
}
</script>
<template>
    <div class="w-full min-h-screen pb-12">
        <!-- Header Section -->
        <section class="w-full min-h-[30vh] sm:min-h-[40vh] bg-gradient-to-br from-[#20aab9] to-[#1a8a97] flex flex-col items-center justify-center text-white py-16 sm:py-24 px-4">
            <h1 class="font-satoshibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-4 text-center">Author Guidelines</h1>
            <p class="font-satoshi text-lg sm:text-xl md:text-2xl text-center">The Journal of Undergraduate Inquiry</p>
        </section>

        <!-- Guidelines Content -->
        <section class="w-full max-w-5xl mx-auto flex flex-col space-y-6 justify-center pt-12 sm:pt-16 px-4 sm:px-6 font-satoshi text-base sm:text-lg">
            <div class="w-full flex flex-col space-y-2 border-b border-gray-200 pb-4">
                <button 
                    id="Overview" 
                    class="flex items-center space-x-3 font-satoshibold text-xl sm:text-2xl text-[#012331] hover:text-[#e68c74] transition-colors duration-200 py-3" 
                    @click="expandsection('Overview')"
                >
                    <svg v-if="currentSectionsExpanded.includes('Overview')" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
                    </svg>
                    <span>General Overview</span>
                </button>
                <Transition name="slide-fade">
                    <div v-if="currentSectionsExpanded.includes('Overview')" class="text-balance pl-9 sm:pl-12 py-4 text-[#012331] leading-relaxed">
                        <p> 
                            Our editorial team is composed of undergraduate students from numerous specialties who review manuscripts to ensure they adhere to guidelines regarding eligibility and formatting. Manuscripts that fit our guidelines will be approved for specialty-specific review by faculty members to assess the quality of work and the significance of the contribution to the field. This two-tiered review process ensures that published works meet the high standards of academic excellence and rigor. 
                        </p>
                        <p class="mt-4">
                            Our team is dedicated to supporting authors throughout the submission and review process, providing constructive feedback to help refine and enhance their work. We believe in the importance of mentorship and collaboration, and our faculty reviewers play a crucial role in guiding students to produce their best possible work. 
                        </p>
                        <p class="mt-4">
                            Ultimately, our goal is to create a supportive and dynamic environment where undergraduate students can develop their research skills, gain valuable experience in academic publishing, and contribute meaningfully to their academic communities.
                        </p>
                    </div>
                </Transition>
            </div>
            <div class="w-full flex flex-col space-y-2 border-b border-gray-200 pb-4">
                <button 
                    id="Submissions" 
                    class="flex items-center space-x-3 font-satoshibold text-xl sm:text-2xl text-[#012331] hover:text-[#e68c74] transition-colors duration-200 py-3" 
                    @click="expandsection('Submissions')"
                >
                    <svg v-if="currentSectionsExpanded.includes('Submissions')" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
                    </svg>
                    <span>Submission Eligibility</span>
                </button>
                <Transition name="slide-fade">
                    <div v-if="currentSectionsExpanded.includes('Submissions')" class="text-balance flex flex-col justify-start pl-9 sm:pl-12 py-4 text-[#012331] leading-relaxed">
                        <p class="mb-6"> 
                            We accept submissions throughout the year and will process manuscripts for publishing in periodic intervals. The eligibility, quality, and significance of a manuscript will be graded based upon the following requirements: 
                        </p>
                        <ol class="space-y-6 list-decimal list-inside">
                            <li>
                                <span class="font-satoshibold">Originality:</span>
                                <span class="ml-2">The manuscript must present original research, creative work, or scholarly insights that have not been previously published elsewhere.</span>
                            </li>
                            <li>
                                <span class="font-satoshibold">Intellectual Rigor:</span> 
                                <span class="ml-2">The work must demonstrate thorough research, methodology, and logical argumentation. For creative submissions, there should be a clear articulation of purpose and technique.</span>
                            </li>
                            <li>
                                <span class="font-satoshibold">Significance:</span> 
                                <span class="ml-2">The manuscript should exhibit a valued and meaningful contribution to the desired field. Submitted work is expected to align with current fields of discussion and growth while introducing novel theories, concepts, or methodologies that push the boundaries of traditional thinking and open new avenues for exploration.</span>
                            </li>
                            <li>
                                <span class="font-satoshibold">Mentorship and Academic Validation:</span>
                                <span class="ml-2">The manuscript must have either been supervised by a faculty member, or validated coursework from a university-run academic course graded by a faculty member.</span>  
                            </li>             
                        </ol>
                    </div>
                </Transition>
            </div>
            <div class="w-full flex flex-col space-y-2 border-b border-gray-200 pb-4">
                <button 
                    id="Sections" 
                    class="flex items-center space-x-3 font-satoshibold text-xl sm:text-2xl text-[#012331] hover:text-[#e68c74] transition-colors duration-200 py-3" 
                    @click="expandsection('Sections')"
                >
                    <svg v-if="currentSectionsExpanded.includes('Sections')" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
                    </svg>
                    <span>Journal Sections</span>
                </button>
                <Transition name="slide-fade">
                    <div v-if="currentSectionsExpanded.includes('Sections')" class="text-balance flex flex-col space-y-6 justify-start pl-9 sm:pl-12 py-4 text-[#012331] leading-relaxed">
                        <div>
                            <p class="font-satoshibold mb-2">
                                Science and Medicine
                            </p>
                            <p>
                                This journal is dedicated to highlighting the latest advancements and discoveries in the fields of biology, chemistry, physics, health sciences, and medicine. Each journal edition will highlight groundbreaking and novel discoveries in the fields of science and medicine, showcasing the world-class research conducted by undergraduate students.
                            </p>
                        </div>
                        <div>
                            <p class="font-satoshibold mb-2">
                                Arts and Humanities
                            </p>
                            <p>
                                This journal is dedicated to showcasing the creative and scholarly achievements of undergraduate students in the fields of arts and humanities. Each edition will feature original work in literature, history, philosophy, visual arts, cultural studies, and other related disciplines. Our goal is to provide a spotlight for thought-provoking contributions of undergraduate students to foster a setting for diverse perspectives and ideas.
                            </p>
                        </div>
                    </div>
                </Transition>
            </div>
            <div class="w-full flex flex-col space-y-2 border-b border-gray-200 pb-4">
                <button 
                    id="Formatting" 
                    class="flex items-center space-x-3 font-satoshibold text-xl sm:text-2xl text-[#012331] hover:text-[#e68c74] transition-colors duration-200 py-3" 
                    @click="expandsection('Formatting')"
                >
                    <svg v-if="currentSectionsExpanded.includes('Formatting')" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
                    </svg>
                    <span>Submission Formatting</span>
                </button>
                <Transition name="slide-fade">
                    <div v-if="currentSectionsExpanded.includes('Formatting')" class="text-balance flex flex-col justify-start pl-9 sm:pl-12 py-4 text-[#012331] leading-relaxed">
                        <p> 
                            The manuscript must be submitted in a PDF file with 12 point Times New Roman font, with all citations in Vancouver citation formatting with in-text superscripts.
                        </p>
                        <p class="mt-4">
                            Any instances of copyright or plagiarism will be immediately rejected. All supplemental tables and data must be submitted in a separate Microsoft Excel or Google Sheets file.
                        </p>
                        <p class="mt-4">
                            All figures and tables must contain figure titles and captions and must be referred to with the figure title. The names of all contributors, including authors and supervisors must be included at the top of the document. Affiliations of all authors must also be stated, including department and faculty.
                        </p>
                        <p class="mt-4">
                            Submissions can be expected to be read by undergraduate students of all disciplines. As such, writing should avoid technical terms that may be unrecognizable to those outside a specific discipline.
                        </p>
                        <p class="mt-4">
                            All research-based submissions must include the following sections: abstract, introduction, methods, results, discussion, conclusions, acknowledgements, references. While no strict template is provided, all non-research manuscripts must follow a logical flow of presentation which is conducive to the understanding of the reader.
                        </p>
                    </div>
                </Transition>
            </div>
            <div class="w-full flex flex-col space-y-2 border-b border-gray-200 pb-4">
                <button 
                    id="Review" 
                    class="flex items-center space-x-3 font-satoshibold text-xl sm:text-2xl text-[#012331] hover:text-[#e68c74] transition-colors duration-200 py-3" 
                    @click="expandsection('Review')"
                >
                    <svg v-if="currentSectionsExpanded.includes('Review')" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
                    </svg>
                    <span>Review Process</span>
                </button>
                <Transition name="slide-fade">
                    <div v-if="currentSectionsExpanded.includes('Review')" class="text-balance flex flex-col justify-start pl-9 sm:pl-12 py-4 text-[#012331] leading-relaxed">
                        <p> 
                            Once a manuscript is submitted, an initial review team ensures that each manuscript fulfills the requirements outlined above. 
                            If a manuscript is accepted for revisions, the manuscript will undergo a thorough peer-review process where 2-3 undergraduate students of related and unrelated disciplines to the manuscript at hand will provide feedback. 
                            Once feedback is provided back to the author, the author will have two weeks to re-submit the manuscript for final review. From this point, the manuscript will either be selected for publication or rejected.
                        </p>
                        <p class="mt-4">
                            The JUI will publish copies of the Science and Medicine, and Arts and Humanities journals twice per annum. Authors are encouraged to resubmit rejected work following revisions, however, are limited to one submission per journal period.
                        </p>
                    </div>
                </Transition>
            </div>
        </section>

        <!-- Submission Form Section -->
        <section class="w-full max-w-3xl mx-auto py-12 sm:py-16 px-4 sm:px-6">
            <h2 class="flex items-center justify-center font-satoshibold text-2xl sm:text-3xl md:text-4xl text-[#012331] mb-8 sm:mb-12">
                Submission Form
            </h2>
            <div class="flex items-center justify-center font-satoshi">
                <form @submit.prevent="emailForm(form)" class="w-full flex flex-col items-center space-y-4 sm:space-y-6 bg-white shadow-xl rounded-xl p-6 sm:p-8">
                    <div class="w-full sm:w-[90%] h-14 border-2 border-gray-300 rounded-lg flex items-center transition-all duration-200 focus-within:border-[#e68c74] focus-within:ring-2 focus-within:ring-[#e68c74]/20">
                            <label for="name" class="pl-2">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6 text-gray-500">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                                </svg>
                            </label>
                            <input id="name" placeholder="Full Name" class="border-l-2 ml-2 border-gray-300 pl-3 w-full bg-transparent focus:outline-none" v-model="form.name" type="text" required>
                        </div>
                        <div class="w-full sm:w-[90%] h-14 border-2 border-gray-300 rounded-lg flex items-center transition-all duration-200 focus-within:border-[#e68c74] focus-within:ring-2 focus-within:ring-[#e68c74]/20">
                            <label for="email" class="pl-4">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-500">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                                </svg>
                            </label>
                            <input id="email" placeholder="Email Address" class="border-l-2 ml-2 border-gray-300 pl-3 w-full bg-transparent focus:outline-none" v-model="form.email" type="email" required>
                        </div>
                        <div class="w-full sm:w-[90%] h-14 border-2 border-gray-300 rounded-lg flex items-center transition-all duration-200 focus-within:border-[#e68c74] focus-within:ring-2 focus-within:ring-[#e68c74]/20">
                            <label for="faculty" class="pl-4">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-500">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" />
                                </svg>
                            </label>
                            <select id="faculty" class="border-l-2 ml-2 border-gray-300 pl-3 w-full bg-transparent focus:outline-none" v-model="form.faculty" required>
                                <option value="" disabled selected>Select Faculty</option>
                                <option value="Faculty of Science">Faculty of Science</option>
                                <option value="Faculty of Medicine">Faculty of Medicine</option>
                                <option value="Faculty of Arts">Faculty of Arts</option>
                                <option value="Faculty of Business">Faculty of Business</option>
                                <option value="Faculty of Education">Faculty of Education</option>
                                <option value="Faculty of Engineering">Faculty of Engineering</option>
                                <option value="Faculty of Law">Faculty of Law</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        <div class="w-full sm:w-[90%] h-14 border-2 border-gray-300 rounded-lg flex items-center transition-all duration-200 focus-within:border-[#e68c74] focus-within:ring-2 focus-within:ring-[#e68c74]/20">
                            <label for="section" class="pl-4">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-500">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                                </svg>
                            </label>
                            <select id="section" class="border-l-2 ml-2 border-gray-300 pl-3 w-full bg-transparent focus:outline-none" v-model="form.section" required>
                                <option value="" disabled selected>Select Section</option>
                                <option value="Science and Medicine">Science and Medicine</option>
                                <option value="Arts and Humanities">Arts and Humanities</option>
                            </select>
                        </div>
                        <div class="w-full sm:w-[90%]">
                            <h3 class="text-lg sm:text-xl font-satoshibold mt-6 mb-4 text-[#012331]">Upload your PDF:</h3>
                        </div>
                        <div class="w-full sm:w-[90%] border-2 border-dashed border-gray-300 rounded-lg transition-all duration-200" :class="{ 'border-[#e68c74] bg-[#e68c74]/5': isDragOver }">
                            <div class="container mx-auto p-4">
                                <div 
                                    @dragover.prevent="onDragOver" 
                                    @drop.prevent="onDrop" 
                                    @dragenter.prevent="dragEnter" 
                                    @dragleave.prevent="dragLeave" 
                                    class="drag-area rounded-lg p-6 sm:p-8 text-center cursor-pointer"
                                >
                                    <div class="flex flex-col justify-center items-center space-y-4" @click="browseFiles">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-12 h-12 text-gray-400">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M9 8.25H7.5a2.25 2.25 0 0 0-2.25 2.25v9a2.25 2.25 0 0 0 2.25 2.25h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25H15m0-3-3-3m0 0-3 3m3-3V15" />
                                        </svg>
                                        <p class="text-gray-600 font-satoshi">
                                            Drag & Drop or <span class="text-[#e68c74] underline font-satoshibold">browse</span> to upload PDF files
                                        </p>
                                    </div>
                                    <input type="file" ref="fileInput" hidden @change="onFileChange" accept="application/pdf" multiple />
                                    <div v-for="(file, index) in selectedFile" :key="index" class="mt-4 flex flex-col justify-center items-center space-y-2">
                                        <p class="text-[#012331] font-satoshi">Selected file: <span class="font-satoshibold">{{ (file as File).name }}</span></p>
                                        <button 
                                            type="button"
                                            @click="removeFile(index)" 
                                            class="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors duration-200 text-sm font-satoshibold"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                                            </svg>
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="pt-4">
                            <button
                                type="submit"
                                :disabled="isSubmitting"
                                class="bg-[#e68c74] px-8 py-3 rounded-lg text-white font-satoshibold text-lg transition-all duration-300 hover:bg-[#e68c74]/90 hover:shadow-lg active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100"
                            >
                                <span v-if="isSubmitting" class="flex items-center gap-3">
                                    <svg class="h-5 w-5 animate-spin text-white" viewBox="0 0 24 24" aria-hidden="true">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" />
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
                                    </svg>
                                    Submitting...
                                </span>
                                <span v-else>Submit</span>
                            </button>
                        </div>
                    </form>
            </div>
            <Transition name="fade">
                <div v-if="fileAdded && emailSent" class="w-full flex items-center justify-center font-satoshibold mt-8">
                    <div class="w-fit flex items-center justify-center p-6 rounded-lg shadow-lg bg-green-50 border-2 border-green-200">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-7 h-7 text-green-600">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                        <p class="pl-3 text-green-700">Your submission was successfully added</p>
                    </div>
                </div>
            </Transition>
        </section>
    </div>
</template>

<style scoped>
input:focus,
select:focus {
  outline: none;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.drag-area {
  cursor: pointer;
  transition: background-color 0.2s ease;
}
</style>